import * as React from 'react';

import { isAuthenticatedUser } from 'js/lib/user';

import { isAnnualByVariantId as isAnnual } from 'bundles/coursera-plus-common/constants/CourseraPlusProductVariant';
import type { CourseraPlusCtaPart, Maybe } from 'bundles/coursera-plus-landing-page/__generated__/baseContentfulTypes';
import CourseraPlusAnnualWithFreeTrialContainer from 'bundles/coursera-plus-landing-page/components/annualWithFreeTrial/CourseraPlusAnnualWithFreeTrialContainer';
import CTAContentfulLine from 'bundles/coursera-plus-landing-page/components/ctaContentful/CTAContentfulLine';
import {
  CTA_LOCATIONS,
  HIDDEN_SLUG_FOR_DEFAULT_LANDING_PAGE,
  PRESENTATIONS,
  PRODUCT_CHOICES,
  artificialCtaLines,
  isPieceForCheckout,
  showPart,
} from 'bundles/coursera-plus-landing-page/contentful/constants';
import type { CourseraPlusCtaPartPicked } from 'bundles/coursera-plus-landing-page/contentful/fetchContentful';
import { useLandingPageExtended } from 'bundles/coursera-plus-landing-page/contentfulData/useLandingPageData';
import { useIsCourseraPlusPromoPage } from 'bundles/coursera-plus-landing-page/utils';
import { previewCourseraPlusAnnualWithFreeTrialTestVariant } from 'bundles/coursera-plus-landing-page/utils/courseraPlusAnnualWithFreeTrial/annualWithFreeTrialUtils';
import { useProductVariantUrlAction } from 'bundles/coursera-plus-landing-page/utils/useProductVariantUrlAction';
import { ENROLL } from 'bundles/enroll-common/utils/enrollActionParams';

import _t from 'i18n!nls/coursera-plus-landing-page';

export type PropsFromCaller = {
  ctaPieces: Array<Maybe<CourseraPlusCtaPart>> | undefined;
  ctaLocation: string;
};

export type CtaLine = {
  lineNumber: number;
  firstLineWithCheckout: boolean;
  pieces: Array<CourseraPlusCtaPartPicked>;
};

const BUTTON_ONLY_CTA_LOCATIONS = [CTA_LOCATIONS.learnerStats, CTA_LOCATIONS.collections, CTA_LOCATIONS.sticky];

const CTAContentful = (props: PropsFromCaller) => {
  const { ctaPieces, ctaLocation } = props;
  const landingPageExtended = useLandingPageExtended();
  const { action, variant, redirectToCheckout } = useProductVariantUrlAction();
  const isPromoPage = useIsCourseraPlusPromoPage();
  const {
    userHasCourseraPlusFreeTrial,
    isCourseraPlusOwner,
    isCourseraLiteOwner,
    primaryProductItemId,
    secondaryProductItemId,
    landingPage,
  } = landingPageExtended;
  const isDefaultLandingPage = landingPage.slug === HIDDEN_SLUG_FOR_DEFAULT_LANDING_PAGE;

  let lines: Array<CtaLine> = [];
  if (isCourseraPlusOwner) {
    lines = artificialCtaLines(_t('Search Coursera Plus'));
  } else if (isCourseraLiteOwner && !isCourseraPlusOwner) {
    const ctaButtonLine = { lineNumber: 1, firstLineWithCheckout: false, pieces: [] } as CtaLine;
    const ctaMonthlyPriceLine = { lineNumber: 2, firstLineWithCheckout: false, pieces: [] } as CtaLine;
    const ctaLinkLine = { lineNumber: 3, firstLineWithCheckout: false, pieces: [] } as CtaLine;

    if (primaryProductItemId) {
      const buttonPiece = {
        product: PRODUCT_CHOICES.primary,
        presentation: PRESENTATIONS.bigButton,
        freeform: _t('Upgrade to Coursera Plus'),
      };
      ctaButtonLine.pieces.push(buttonPiece);
      lines.push(ctaButtonLine);

      if (!BUTTON_ONLY_CTA_LOCATIONS.includes(ctaLocation)) {
        const textPiece = {
          product: PRODUCT_CHOICES.primary,
          presentation: PRESENTATIONS.plain,
          pretranslated: 'price/time unit, cancel anytime (original price)',
        };
        ctaMonthlyPriceLine.pieces.push(textPiece);
        lines.push(ctaMonthlyPriceLine);

        if (secondaryProductItemId && ctaLocation === CTA_LOCATIONS.hero) {
          const linkPiece = {
            product: PRODUCT_CHOICES.secondary,
            presentation: PRESENTATIONS.link,
            displayAsBold: true,
            ...(isAnnual(secondaryProductItemId)
              ? { pretranslated: 'Upgrade to Coursera Plus Annual (original price)' }
              : { freeform: _t('Upgrade to Coursera Plus') }),
          };
          ctaLinkLine.pieces.push(linkPiece);
          lines.push(ctaLinkLine);
        }
      }
    }
  } else {
    // compile lines from the pieces
    for (const piece of ctaPieces ?? []) {
      if (piece && showPart(piece, userHasCourseraPlusFreeTrial)) {
        const lineNumber = piece.line || 1;
        let line = lines.find((l) => l.lineNumber === lineNumber);
        if (!line) {
          line = { lineNumber, firstLineWithCheckout: false, pieces: [] };
          lines.push(line);
        }
        line.pieces.push(piece as CourseraPlusCtaPartPicked);
      }
    }
  }
  if (ctaLocation === CTA_LOCATIONS.hero && isCourseraLiteOwner) {
    if (lines[0]) lines[0].lineNumber = 2; // button
    if (lines[1]) lines[1].lineNumber = 1; // monthly price
    if (lines[2]) lines[2].lineNumber = 3; // yearly price link
  }

  lines.sort((line1, line2) => line1.lineNumber - line2.lineNumber);

  if (ctaLocation === CTA_LOCATIONS.footer && !isCourseraLiteOwner && !isPromoPage) {
    lines.reverse();
  }

  let checkoutFound = false;
  for (let i = 0; i < lines.length; i += 1) {
    for (const piece of lines[i].pieces) {
      if (isPieceForCheckout(piece)) {
        lines[i].firstLineWithCheckout = true;
        checkoutFound = true;
        break;
      }
    }
    if (checkoutFound) {
      break;
    }
  }

  const isAnnualWithFreeTrialTest = ['2', '3'].includes(
    previewCourseraPlusAnnualWithFreeTrialTestVariant(isDefaultLandingPage)
  );

  if (
    isAuthenticatedUser() &&
    ctaLocation === CTA_LOCATIONS.hero &&
    isDefaultLandingPage &&
    isAnnualWithFreeTrialTest &&
    !isCourseraPlusOwner &&
    !variant &&
    action !== ENROLL &&
    !redirectToCheckout
  ) {
    return <CourseraPlusAnnualWithFreeTrialContainer isCourseraLiteOwner={isCourseraLiteOwner} />;
  }

  return (
    <div>
      {lines.map((line) => {
        return <CTAContentfulLine key={line.lineNumber} line={line} ctaLocation={ctaLocation} />;
      })}
    </div>
  );
};

export default CTAContentful;

export const CTAContentfulForHero = () => {
  const landingPageExtended = useLandingPageExtended();
  return (
    <CTAContentful
      ctaPieces={landingPageExtended.landingPage.heroCtaPartsCollection?.items}
      ctaLocation={CTA_LOCATIONS.hero}
    />
  );
};
